import {trace, Span} from '@opentelemetry/api';
import {NextRouter} from 'next/router';
import {addContextToSpan} from './user-context';
import {hrTime} from '@opentelemetry/core';
import {forceFlushTraces} from './index';

// Add NodeJS namespace for Timeout
// <reference types="node" />

// Define the Timeout type
type Timeout = ReturnType<typeof setTimeout>;

// Define HrTime type (seconds, nanoseconds tuple)
type HrTime = [number, number];

const TRACER_NAME = 'nextjs-router';
const DEBOUNCE_TIME = 50; // ms

const getRouterTracer = () => {
  return trace.getTracer(TRACER_NAME);
};

// Store the current route info
interface RouteInfo {
  pathname: string;
  asPath: string;
  query: Record<string, any>;
}

let currentRoute: RouteInfo = {
  pathname: '',
  asPath: '',
  query: {},
};

/**
 * Sets up instrumentation for Next.js router events
 * Call this in your _app.tsx useEffect
 * @param {NextRouter} router The Next.js router instance to instrument
 * @return {Function} A cleanup function that removes event listeners when called
 */
export const instrumentNextJsRouter = (router: NextRouter): (() => void) => {
  if (typeof window === 'undefined') return () => {};

  // Update the current route on load
  currentRoute = {
    pathname: router.pathname,
    asPath: router.asPath,
    query: router.query,
  };

  // Create a closure to store the active spans
  let activeChangeSpan: Span | null = null;
  let routeChangeTimeout: Timeout | null = null;
  let navigationCount = 0;
  let startTime: HrTime;

  // Track route change start - with debouncing
  const handleRouteChangeStart = (url: string) => {
    navigationCount++;

    // Clear any existing timeout
    if (routeChangeTimeout) {
      clearTimeout(routeChangeTimeout);
    }

    // Finish any existing span
    if (activeChangeSpan) {
      activeChangeSpan.setAttribute('page.route_change_cancelled', true);
      activeChangeSpan.end();
      activeChangeSpan = null;
    }

    // Debounce route changes to avoid creating spans for intermediate states
    routeChangeTimeout = setTimeout(() => {
      const tracer = getRouterTracer();
      activeChangeSpan = tracer.startSpan('route_change_start');

      // Capture the start time in high resolution
      startTime = hrTime();

      activeChangeSpan.setAttribute('page.route_from', currentRoute.pathname);
      activeChangeSpan.setAttribute('page.url_from', currentRoute.asPath);
      activeChangeSpan.setAttribute('page.route_to', url);
      activeChangeSpan.setAttribute('page.navigation_count', navigationCount);

      // Add user/session context
      addContextToSpan(activeChangeSpan);

      routeChangeTimeout = null;
    }, DEBOUNCE_TIME);
  };

  // Track route change complete
  const handleRouteChangeComplete = async (url: string) => {
    // Clear any existing timeout
    if (routeChangeTimeout) {
      clearTimeout(routeChangeTimeout);
      routeChangeTimeout = null;
    }

    if (activeChangeSpan) {
      // Calculate the duration using hrTime difference
      const currentTime = hrTime();
      const duration = calculateDurationInMillis(startTime, currentTime);

      activeChangeSpan.setAttribute('page.route_to', url);
      activeChangeSpan.setAttribute('page.navigation_duration_ms', duration);
      activeChangeSpan.end();
      activeChangeSpan = null;

      // Update the current route
      currentRoute = {
        pathname: router.pathname,
        asPath: router.asPath,
        query: router.query,
      };

      // Force flush spans to ensure they are sent to collector
      try {
        await forceFlushTraces();
      } catch (error) {
        console.warn('[otel-router] Failed to flush spans on route change:', error);
      }
    }

    // Create a new span for page view
    const tracer = getRouterTracer();
    const pageViewSpan = tracer.startSpan('page_view');

    pageViewSpan.setAttribute('page.route', router.pathname);
    pageViewSpan.setAttribute('page.url', url);

    // Add query params count (useful for performance tracking)
    const queryParamsCount = Object.keys(router.query).length;
    pageViewSpan.setAttribute('page.query_params_count', queryParamsCount);

    // Reset navigation counter for the next navigation sequence
    navigationCount = 0;

    // Add user/session context
    addContextToSpan(pageViewSpan);

    // End the span immediately since it's just marking the page view
    pageViewSpan.end();
  };

  // Track route change errors
  const handleRouteChangeError = (err: Error, url: string) => {
    // Clear any existing timeout
    if (routeChangeTimeout) {
      clearTimeout(routeChangeTimeout);
      routeChangeTimeout = null;
    }

    // If we have an active span from route change start, end it with error
    if (activeChangeSpan) {
      activeChangeSpan.setAttribute('error', true);
      activeChangeSpan.setAttribute('error.type', err.name);
      activeChangeSpan.setAttribute('error.message', err.message);
      activeChangeSpan.setAttribute('error.stack', err.stack || '');
      activeChangeSpan.end();
      activeChangeSpan = null;
    }

    // Create a dedicated error span
    const tracer = getRouterTracer();
    const errorSpan = tracer.startSpan('route_change_error');

    errorSpan.setAttribute('page.route_intended', url);
    errorSpan.setAttribute('error.type', err.name);
    errorSpan.setAttribute('error.message', err.message);
    errorSpan.setAttribute('error.stack', err.stack || '');

    // Add user/session context
    addContextToSpan(errorSpan);

    errorSpan.end();
  };

  // Subscribe to router events
  router.events.on('routeChangeStart', handleRouteChangeStart);
  router.events.on('routeChangeComplete', handleRouteChangeComplete);
  router.events.on('routeChangeError', handleRouteChangeError);

  // Return unsubscribe function that also cleans up any active spans
  return () => {
    router.events.off('routeChangeStart', handleRouteChangeStart);
    router.events.off('routeChangeComplete', handleRouteChangeComplete);
    router.events.off('routeChangeError', handleRouteChangeError);

    // Clean up any active span
    if (activeChangeSpan) {
      activeChangeSpan.setAttribute('page.route_change_cancelled', true);
      activeChangeSpan.end();
    }

    // Clear any pending timeouts
    if (routeChangeTimeout) {
      clearTimeout(routeChangeTimeout);
    }
  };
};

/**
 * Calculate duration in milliseconds between two HrTime values
 * @param {HrTime} startTime - The starting high-resolution time
 * @param {HrTime} endTime - The ending high-resolution time
 * @return {number} Duration in milliseconds
 */
function calculateDurationInMillis(startTime: HrTime, endTime: HrTime): number {
  // HrTime is [seconds, nanoseconds]
  const seconds = endTime[0] - startTime[0];
  const nanoseconds = endTime[1] - startTime[1];

  // Convert to milliseconds
  return seconds * 1000 + nanoseconds / 1000000;
}

// Add route span type to Window
declare global {
  interface Window {
    __otelRouteChangeSpan?: any;
  }
}
